import { useState } from 'react';
import { Icon } from '@iconify/react';
import { MessageSquare, Brain, Users, Shield, Play, Database, Zap } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function DiscordLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Install Memara Bot in Discord
/invite memara-bot

# 2. Connect to your server
@Memara setup server

# 3. Start capturing memories
@Memara remember this important discussion

# 4. Search server knowledge
@Memara search "project updates"`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=dQw4w9WgXcQ', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="discord_bot"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-50 shadow" aria-label="Discord Logo">
          <Icon icon="logos:discord-icon" width={48} height={48} />
        </span>
      }
      title="Discord Integration"
      description="Community intelligence for Discord servers and gaming communities. Transform gaming discussions, project collaborations, and community conversations into searchable knowledge with intelligent context understanding and collaborative memory management."
      features={[
        { icon: <MessageSquare className="h-4 w-4 text-indigo-600" />, title: 'Intelligent Chat Analysis', description: 'Extract and contextualize key information from Discord conversations with AI-powered content understanding and community insights.' },
        { icon: <Brain className="h-4 w-4 text-indigo-600" />, title: 'Gaming & Community Context', description: 'AI-powered comprehension of gaming strategies, community dynamics, and project collaboration with specialized context understanding.' },
        { icon: <Users className="h-4 w-4 text-indigo-600" />, title: 'Server Knowledge Management', description: 'Shared community memory with role-based access controls and collaborative intelligence for gaming teams and project communities.' },
        { icon: <Shield className="h-4 w-4 text-indigo-600" />, title: 'Privacy-First Community Data', description: 'Secure handling of community discussions with multi-tenant isolation and privacy-focused memory management.' },
        { icon: <Database className="h-4 w-4 text-indigo-600" />, title: 'Multi-Server Intelligence', description: 'Cross-server knowledge management with organized memory spaces and intelligent content categorization.' },
        { icon: <Zap className="h-4 w-4 text-indigo-600" />, title: 'Real-time Memory Capture', description: 'Instant memory creation and search with sub-200ms response times for seamless community workflow integration.' },
      ]}
      setupSteps={[
        'Install Memara Bot in your Discord server',
        'Connect and configure server permissions and roles',
        'Start capturing community knowledge and discussions',
        'Search and access server memory instantly with intelligent context',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Icon icon="logos:discord-icon" width={20} height={20} />,
        },
      ]}
      videoUrl="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
      microcopy="Trusted by gaming communities, project teams, and Discord server administrators using Discord for collaborative community management and knowledge sharing."
      trustedBy="Discord, Gaming Communities, and more."
      testimonial="Memara's Discord integration transforms our gaming community into an intelligent knowledge base that captures strategies, maintains project context, and enhances our collaborative intelligence across all server discussions."
    />
  );
} 